import { useQueryClient } from "@tanstack/react-query";
import { Check, FileText, Image as ImageIcon, Info, RefreshCw } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useOutletContext } from "react-router";
import type { ImportFileDto, ImportJobDto, MenuSummaryDto } from "../../../shared/api-types";
import { importApi, keys, useImports, useMenus } from "../../api/queries";
import { FlowSteps } from "../../components/FlowSteps";
import { PagePreview, type PagePreviewActions } from "../../components/PagePreview";
import { useToast } from "../../components/Toast";
import { UploadZone } from "../../components/UploadZone";
import { Alert, ICON } from "../../components/ui";
import { plural } from "../../lib/format";
import { importFileError, MAX_IMPORT_FILES, sizeLabel } from "../../lib/importFiles";
import {
  isCancelled,
  needsAttention,
  type PrepSource,
  usePagePreparation,
} from "../../lib/prepare/usePagePreparation";
import type { ShellContext } from "../AppShell";
import { importResultLine, importStage, STAGE_TEXT } from "./importState";
import { RetakePages } from "./RetakePages";

// "Import AI" (prototype page `import`), first step of the fast flow (spec §1A "Încarcă"). Progress comes from
// the server's job and file states; there is no simulated progress. Without an AI provider (B-07) the job ends
// "blocked" and the page says so. A job started here (or by the onboarding) continues straight to "Verifică" when
// its draft is ready; an older job found on a later visit does not redirect.

const ACTIVE = new Set(["uploading", "queued", "processing"]);
const FINISHED_FILE = new Set(["done", "failed", "blocked"]);
const PHASES = [
  "Se încarcă fișierele",
  "Analizăm documentele",
  "Extragem produsele și prețurile",
  "Verificăm informațiile",
];

const FILE_STATUS: Record<ImportFileDto["status"], string> = {
  pending: "În așteptare",
  processing: "Se citește…",
  done: "Citit",
  failed: "Eșuat",
  blocked: "În așteptarea extragerii automate",
};

function phaseIndex(job: ImportJobDto): number {
  if (job.status === "uploading") return 0;
  if (job.status === "queued") return 1;
  const finished = job.files.filter((f) => FINISHED_FILE.has(f.status)).length;
  return finished === job.files.length ? 3 : 2;
}

function FileList({ files }: { files: ImportFileDto[] }) {
  // Pages of one document share its name; the page number tells them apart.
  const shared = (f: ImportFileDto) => files.some((o) => o.id !== f.id && o.name === f.name);
  return (
    <ul className="import-files">
      {files.map((f) => (
        <li key={f.id}>
          <span className="import-file-icon" aria-hidden="true">
            {f.contentType === "application/pdf" || f.page ? <FileText {...ICON} /> : <ImageIcon {...ICON} />}
          </span>
          <span className="stack" style={{ gap: 2, flex: 1, minWidth: 0 }}>
            <span style={{ fontWeight: 600, overflowWrap: "anywhere" }}>
              {f.name}
              {f.page && shared(f) ? ` · pagina ${f.page}` : ""}
            </span>
            <span className="caption muted">
              {sizeLabel(f.bytes)} · {FILE_STATUS[f.status]}
            </span>
            {f.error && f.status === "failed" && <span className="caption field-error">{f.error}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function ImportPage() {
  const { membership } = useOutletContext<ShellContext>();
  const rid = membership.restaurant.id;
  const canEdit = membership.role !== "staff";
  const api = importApi(rid);
  const qc = useQueryClient();
  const toast = useToast();
  const jobs = useImports(rid);
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [english, setEnglish] = useState(true);
  // Where the import goes when the restaurant already has menus: "new", or a menu to update (owner 2026-11-03).
  // Never preselected: mixing two different menus by mistake is worse than one more click.
  const [target, setTarget] = useState<string | null>(null);
  // "Importă alt document" opens the upload again without touching the last import (its pages stay for review).
  const [newImport, setNewImport] = useState(false);
  // Import from a link (owner 2026-11-04): Wolt / a menu page / a PDF link.
  const [link, setLink] = useState("");
  const menus = useMenus(rid);
  const menuOf = (j: ImportJobDto): MenuSummaryDto | undefined => menus.data?.find((m) => m.id === j.menuId);
  const activeMenus = (menus.data ?? []).filter((m) => !m.archived && m.productCount > 0);
  const prep = usePagePreparation();
  // The job this visit is following (started here, by the onboarding, or seen while running).
  const follow = useRef<string | undefined>((location.state as { follow?: string } | null)?.follow);

  const latest = jobs.data?.[0];
  const job =
    latest && latest.status !== "cancelled" && !(newImport && !ACTIVE.has(latest.status))
      ? latest
      : undefined;
  const refresh = () => qc.invalidateQueries({ queryKey: keys.imports(rid) });

  useEffect(() => {
    if (!job) return;
    if (ACTIVE.has(job.status)) follow.current = job.id;
    else if (job.status === "ready" && job.menuId && !job.error && follow.current === job.id && canEdit) {
      navigate(`/app/verifica/${job.id}`, { replace: true });
    }
  }, [job, canEdit, navigate]);

  /** Choosing documents prepares them in the browser; the import goes on by itself unless a page needs a look. */
  /** Checks chosen files before anything is prepared; returns false (and says why) when one is refused. */
  function accept(list: File[]): boolean {
    setError("");
    if (list.length > MAX_IMPORT_FILES) {
      setError(`Poți încărca cel mult ${MAX_IMPORT_FILES} documente o dată.`);
      return false;
    }
    const invalid = list.map(importFileError).find(Boolean);
    if (invalid) setError(invalid);
    return !invalid && list.length > 0;
  }

  /**
   * Photos chosen together from the phone, or files from the disk, are the whole menu: the import goes on by
   * itself unless a page needs a look. A camera photo is one page of a series: the preview waits for the next
   * photo or "Procesează meniul".
   */
  async function start(list: File[], source: PrepSource) {
    if (!accept(list)) return;
    const result = await prep.start(list, source);
    if (
      source !== "camera" &&
      result.phase === "ready" &&
      result.pages.some((p) => p.include) &&
      !needsAttention(result)
    ) {
      await send();
    }
  }

  const previewActions: PagePreviewActions = {
    onToggle: prep.toggle,
    onRotate: (key) => void prep.rotate(key),
    onMove: prep.move,
    onRemove: prep.remove,
    onRetake: (key, photo, source) => {
      if (accept([photo])) void prep.retake(key, photo, source);
    },
    onAdd: (list, source) => {
      if (accept(list)) void prep.add(list, source);
    },
  };

  /** A link: a menu read exactly becomes the import at once; a PDF / photo continues like an uploaded file. */
  async function fromLink(e: FormEvent) {
    e.preventDefault();
    if (!link.trim() || busy) return;
    setError("");
    setBusy(true);
    try {
      const done = await api.fromLink(
        link.trim(),
        english ? ["en"] : [],
        target && target !== "new" ? target : undefined,
      );
      if ("import" in done) {
        follow.current = done.import.id;
        setLink("");
        setNewImport(false);
        await refresh();
      } else {
        const file = await api.linkFile(done.file.url);
        setLink("");
        setBusy(false);
        await start([file], "files");
        return;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
    setBusy(false);
  }

  /** Creates the import, uploads the included pages (image + crops each), then submits it. */
  async function send() {
    setBusy(true);
    let created: ImportJobDto | undefined;
    try {
      created = await api.create(english ? ["en"] : [], target && target !== "new" ? target : undefined);
      follow.current = created.id;
      await prep.upload(api, created.id);
      await api.submit(created.id, prep.snapshot().ms);
      prep.clear();
      setNewImport(false);
    } catch (e) {
      // A half-uploaded job is cancelled so the next attempt can start cleanly (its files are deleted).
      if (created) await api.cancel(created.id).catch(() => undefined);
      if (isCancelled(e)) prep.clear();
      else setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      await refresh();
    }
  }

  async function act(fn: () => Promise<unknown>, done?: string) {
    setBusy(true);
    try {
      await fn();
      if (done) toast(done);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      await refresh();
    }
  }

  // The steps follow the latest import: uploading, checking, then all done once its menu is published.
  const latestStage = job?.status === "ready" ? importStage(job, menuOf(job)) : null;
  const step =
    latestStage === "published" || latestStage === "publishedWithChanges"
      ? 3
      : latestStage === "needsReview" || latestStage === "readyToPublish"
        ? 1
        : 0;
  const header = (
    <div className="stack" style={{ gap: 6 }}>
      <h1 className="h1">Import AI</h1>
      <p className="page-sub" style={{ margin: 0, maxWidth: 680 }}>
        Încarci meniul existent; Meniova pregătește produsele, tu verifici doar ce e marcat și publici când
        ești gata. Meniul nu se publică fără tine.
      </p>
      <FlowSteps current={step} />
    </div>
  );

  if (jobs.isPending) return header;

  if (prep.state.phase !== "idle") {
    return (
      <>
        {header}
        {error && (
          <Alert tone="error" live>
            {error}
          </Alert>
        )}
        <PagePreview
          state={prep.state}
          actions={previewActions}
          onCancel={() => (prep.state.phase === "uploading" ? prep.cancel() : prep.clear())}
          onContinue={busy ? undefined : () => void send()}
        />
        {prep.state.phase === "error" && !busy && (
          <div className="row" style={{ gap: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={prep.clear}>
              Alege alt document
            </button>
          </div>
        )}
      </>
    );
  }

  if (job && ACTIVE.has(job.status)) {
    const total = job.files.length || 1;
    const finished = job.files.filter((f) => FINISHED_FILE.has(f.status)).length;
    const progress = job.status === "uploading" ? 0 : Math.round((finished / total) * 100);
    const current = phaseIndex(job);
    return (
      <>
        {header}
        <section className="card stack" aria-label="Procesare" style={{ gap: 16, maxWidth: 640 }}>
          <FileList files={job.files} />
          <div className="stack" style={{ gap: 6 }}>
            <div className="row" style={{ justifyContent: "space-between", fontSize: 14 }}>
              <span id="imp-pl" style={{ fontWeight: 600 }}>
                {PHASES[current]}…
              </span>
              <span className="muted" style={{ fontVariantNumeric: "tabular-nums" }}>
                {finished} din {job.files.length} {job.files.length === 1 ? "fișier" : "fișiere"}
              </span>
            </div>
            <div
              className="progress"
              role="progressbar"
              aria-labelledby="imp-pl"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div style={{ width: `${progress}%` }} />
            </div>
          </div>
          <ol className="import-phases">
            {PHASES.map((label, k) => (
              <li key={label} className={k < current ? "done" : k === current ? "current" : undefined}>
                <span className="ph-ic" aria-hidden="true">
                  {k < current ? <Check {...ICON} /> : k === current ? <RefreshCw {...ICON} /> : null}
                </span>
                {label}
              </li>
            ))}
          </ol>
          <span className="note">Poți anula oricând; nu se schimbă nimic în meniurile tale.</span>
          {canEdit && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              style={{ alignSelf: "flex-start" }}
              disabled={busy}
              onClick={() =>
                act(() => api.cancel(job.id), "Importul a fost anulat. Fișierele au fost șterse.")
              }
            >
              Anulează importul
            </button>
          )}
        </section>
      </>
    );
  }

  if (job && (job.status === "blocked" || job.status === "failed")) {
    const blocked = job.status === "blocked";
    return (
      <>
        {header}
        <section
          className="card stack"
          role={blocked ? "status" : "alert"}
          style={{ gap: 14, maxWidth: 640 }}
        >
          <Alert
            tone={blocked ? "info" : "error"}
            title={
              blocked
                ? "Importul automat nu a pornit pentru acest document"
                : "Nu am putut procesa documentul"
            }
          >
            {blocked
              ? "Documentele sunt salvate privat; nu s-a extras nimic și nu s-a schimbat nimic în meniurile tale. Motivul apare lângă fiecare fișier. Poți reîncerca mai târziu, adăuga produsele manual sau ne poți scrie."
              : "Meniurile tale nu s-au schimbat. Reîncercarea nu consumă un import nou."}
          </Alert>
          <FileList files={job.files} />
          {canEdit && (
            <div className="row" style={{ gap: 8 }}>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy}
                onClick={() => act(() => api.retry(job.id))}
              >
                <RefreshCw {...ICON} aria-hidden="true" />
                Reîncearcă
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy}
                onClick={() =>
                  act(() => api.cancel(job.id), "Importul a fost anulat. Fișierele au fost șterse.")
                }
              >
                Încarcă alt fișier
              </button>
              <Link className="btn btn-ghost" to="/app/products">
                Creează produse manual
              </Link>
            </div>
          )}
        </section>
      </>
    );
  }

  if (job && job.status === "ready") {
    const failed = job.files.filter((f) => f.status === "failed");
    const stage = importStage(job, menuOf(job));
    const text = STAGE_TEXT[stage];
    const published = stage === "published" || stage === "publishedWithChanges";
    const target = menuOf(job);
    return (
      <>
        {header}
        <section className="card stack" role="status" style={{ gap: 14, maxWidth: 640 }}>
          {job.error ? (
            <Alert tone="error" title="Ciorna nu a putut fi creată">
              {job.error}
            </Alert>
          ) : job.summary ? (
            <Alert tone={text.tone === "error" ? "error" : text.tone} title={text.title}>
              {importResultLine(job.summary)}. {target ? `Meniul: „${target.name}”. ` : ""}
              {stage === "needsReview" || stage === "readyToPublish"
                ? "Nimic nu este publicat încă."
                : stage === "published"
                  ? "Clienții văd acest meniu."
                  : stage === "publishedWithChanges"
                    ? `Clienții văd versiunea publicată; ${plural(target?.pendingChanges ?? 0, "modificare așteaptă", "modificări așteaptă")} publicarea.`
                    : stage === "offline"
                      ? "Meniul a fost publicat, apoi retras: clienții nu îl văd."
                      : stage === "menuGone"
                        ? "Produsele importate nu mai sunt într-un meniu activ."
                        : ""}
            </Alert>
          ) : (
            <Alert tone="success" title="Documentul a fost citit">
              Pregătim ciorna…
            </Alert>
          )}
          {failed.length > 0 && (
            <div className="stack" style={{ gap: 6 }}>
              <strong style={{ fontWeight: 600 }}>
                {plural(failed.length, "pagină nu a putut fi citită", "pagini nu au putut fi citite")}
              </strong>
              <FileList files={failed} />
            </div>
          )}
          {canEdit && (
            <div className="row" style={{ gap: 8 }}>
              {job.menuId && !published && stage !== "menuGone" && (
                <Link className="btn btn-primary" to={`/app/verifica/${job.id}`}>
                  Verifică meniul
                </Link>
              )}
              {published && target && (
                <>
                  <Link className="btn btn-primary" to={`/app/products?menu=${target.id}`}>
                    Deschide meniul
                  </Link>
                  <a
                    className="btn btn-secondary"
                    href={membership.restaurant.publicUrl}
                    target="_blank"
                    rel="noopener"
                  >
                    Vezi meniul public
                  </a>
                </>
              )}
              {failed.length > 0 && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={busy}
                  onClick={() => act(() => api.retry(job.id))}
                >
                  <RefreshCw {...ICON} aria-hidden="true" />
                  Reîncearcă paginile eșuate
                </button>
              )}
            </div>
          )}
          {canEdit && <RetakePages rid={rid} job={job} />}
          <details className="import-pages">
            <summary>Vezi cele {plural(job.files.length, "pagină procesată", "pagini procesate")}</summary>
            <FileList files={job.files} />
          </details>
          {canEdit && (
            <div className="row" style={{ gap: 8 }}>
              {/* The same photos read again into a new draft; this draft and its edits stay as they are. */}
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={busy}
                title="Aceleași fotografii, citite din nou într-o ciornă nouă. Ciorna actuală rămâne."
                onClick={() =>
                  act(() => api.reread(job.id), "Meniul se citește din nou, într-o ciornă nouă.")
                }
              >
                <RefreshCw {...ICON} aria-hidden="true" />
                Citește din nou meniul
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={busy}
                onClick={() => setNewImport(true)}
              >
                Importă alt document
              </button>
            </div>
          )}
        </section>
        <EarlierImports jobs={(jobs.data ?? []).slice(1)} menuOf={menuOf} />
      </>
    );
  }

  return (
    <>
      {header}
      {jobs.isError && (
        <Alert tone="error" live>
          {jobs.error.message}
        </Alert>
      )}
      {canEdit ? (
        <>
          {activeMenus.length > 0 && (
            <fieldset className="card stack target-pick" style={{ gap: 8 }}>
              <legend className="h3" style={{ padding: 0 }}>
                Unde importăm meniul?
              </legend>
              {activeMenus.map((m) => (
                <label key={m.id} className="row" style={{ gap: 8, alignItems: "flex-start" }}>
                  <input
                    type="radio"
                    name="imp-target"
                    checked={target === m.id}
                    disabled={busy}
                    onChange={() => setTarget(m.id)}
                  />
                  <span>
                    <strong style={{ fontWeight: 600 }}>Actualizez „{m.name}”</strong>
                    <span className="caption muted" style={{ display: "block" }}>
                      Pentru o versiune nouă a aceluiași meniu: ce e la fel rămâne neatins; îți arătăm ce e
                      nou, ce s-a schimbat (preț, gramaj) și ce nu mai apare. Nimic nu se șterge fără acordul
                      tău.
                    </span>
                  </span>
                </label>
              ))}
              <label className="row" style={{ gap: 8, alignItems: "flex-start" }}>
                <input
                  type="radio"
                  name="imp-target"
                  checked={target === "new"}
                  disabled={busy}
                  onChange={() => setTarget("new")}
                />
                <span>
                  <strong style={{ fontWeight: 600 }}>Un meniu nou, separat</strong>
                  <span className="caption muted" style={{ display: "block" }}>
                    Pentru un alt meniu (de exemplu băuturi, meniul zilei, evenimente).
                  </span>
                </span>
              </label>
            </fieldset>
          )}
          {activeMenus.length > 0 && !target ? (
            <p className="muted" style={{ margin: 0 }}>
              Alege mai sus unde importăm, apoi încarci documentul.
            </p>
          ) : (
            <>
              <UploadZone busy={busy} error={error} onFiles={(list, source) => void start(list, source)} />
              <form className="link-import" onSubmit={fromLink}>
                <label className="field-label" htmlFor="import-link">
                  Sau importă din link <span className="field-hint">Wolt, pagina meniului sau un PDF</span>
                </label>
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  <input
                    id="import-link"
                    className="field-input"
                    type="url"
                    inputMode="url"
                    placeholder="https://wolt.com/ro/rou/…"
                    value={link}
                    disabled={busy}
                    onChange={(e) => setLink(e.target.value)}
                    style={{ flex: "1 1 260px", minWidth: 0 }}
                  />
                  <button type="submit" className="btn btn-secondary" disabled={busy || !link.trim()}>
                    {busy && link ? "Se citește…" : "Importă din link"}
                  </button>
                </div>
                <span className="caption muted">
                  Prețurile din aplicațiile de livrare sunt adesea mai mari decât în restaurant: le confirmi
                  pe fiecare la Verifică. Glovo și Bolt Food arată meniul doar în aplicație; acolo fă capturi
                  de ecran și încarcă-le mai sus.
                </span>
              </form>
            </>
          )}
          <label className="check-row">
            <input
              type="checkbox"
              checked={english}
              disabled={busy}
              onChange={(e) => setEnglish(e.target.checked)}
            />
            <span>Pregătește și traducerea în engleză (apare clienților doar după ce o aprobi)</span>
          </label>
        </>
      ) : (
        <Alert tone="info">Doar proprietarul sau managerul poate importa un meniu.</Alert>
      )}
      <div className="info-note">
        <Info {...ICON} aria-hidden="true" />
        <span>
          Nu ai meniul la îndemână? Poți <Link to="/app/products">adăuga produsele manual</Link>.
          {newImport && latest && latest.status !== "cancelled" && (
            <>
              {" "}
              <button type="button" className="btn-link" onClick={() => setNewImport(false)}>
                Înapoi la ultimul import
              </button>
            </>
          )}
        </span>
      </div>
      <EarlierImports jobs={(jobs.data ?? []).filter((j) => j.status !== "cancelled")} menuOf={menuOf} />
    </>
  );
}

/** Earlier imports with the state of the menu each one filled (several imports, several publications). */
function EarlierImports({
  jobs,
  menuOf,
}: {
  jobs: ImportJobDto[];
  menuOf: (j: ImportJobDto) => MenuSummaryDto | undefined;
}) {
  const list = jobs.filter((j) => j.status !== "cancelled");
  if (!list.length) return null;
  return (
    <details className="card import-earlier" style={{ maxWidth: 640 }}>
      <summary>Importuri anterioare ({list.length})</summary>
      <ul className="import-files">
        {list.map((j) => {
          const m = menuOf(j);
          return (
            <li key={j.id}>
              <span className="stack" style={{ gap: 2, flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 600 }}>
                  {new Date(j.createdAt).toLocaleDateString("ro-RO", { day: "numeric", month: "long" })}
                  {m ? ` · ${m.name}` : ""}
                </span>
                <span className="caption muted">
                  {STAGE_TEXT[importStage(j, m)].title}
                  {j.summary ? ` · ${plural(j.summary.products, "produs", "produse")}` : ""}
                </span>
              </span>
              {m && !m.archived && (
                <Link className="btn btn-ghost btn-sm" to={`/app/products?menu=${m.id}`}>
                  Deschide
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </details>
  );
}
